/**
 * AA Landing Page Route
 *
 * Landing page for the Account Aggregator flow.
 * Displays information about AA and provides a button to start the consent flow.
 *
 * Route: /aa
 */

import * as React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ImportViaAAButton, TrustBadgeGroup, PartnerLogos } from "@/components/aa";
import { GlobalErrorFallback } from "@/components/error";
import { TRUST_COPY } from "@/constants/trustCopy";
import {
  Shield,
  ArrowLeft,
  Building2,
  Lock,
  Clock,
  ListChecks,
  ShieldCheck,
  Download,
} from "lucide-react";

export const Route = createFileRoute("/aa/")({
  component: AALandingPage,
  errorComponent: GlobalErrorFallback,
});

// ============================================================================
// Feature Item Component
// ============================================================================

interface FeatureItemProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

function FeatureItem({ icon, title, description }: FeatureItemProps) {
  return (
    <div className="flex flex-col items-center text-center gap-3">
      <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
        {icon}
      </div>
      <div>
        <h3 className="font-medium text-foreground">{title}</h3>
        <p className="mt-1 text-sm text-muted-foreground">{description}</p>
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

function AALandingPage() {
  const [returnUrl, setReturnUrl] = React.useState<string | undefined>(
    undefined
  );

  React.useEffect(() => {
    setReturnUrl(`${window.location.origin}/aa/callback`);
  }, []);

  return (
    <div className="container mx-auto max-w-3xl px-4 py-6">
      {/* Header with back button */}
      <div className="mb-6">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Home
        </Link>
      </div>

      {/* Main Card */}
      <Card>
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <Shield className="h-8 w-8 text-primary" />
          </div>
          <CardTitle className="text-2xl">
            Import Your Mutual Fund Holdings
          </CardTitle>
          <p className="mt-2 text-muted-foreground">
            Securely connect your mutual fund accounts using India&apos;s Account
            Aggregator framework
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            3 simple steps &middot; Typically takes less than 2 minutes
          </p>
        </CardHeader>

        <CardContent className="space-y-8">
          {/* CTA Section — prominent, high up */}
          <div className="rounded-lg border border-border bg-muted/30 p-6 text-center">
            <h3 className="text-lg font-semibold text-foreground">
              Ready to get started?
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              You&apos;ll be redirected to Saafe to complete the consent process
            </p>
            <ImportViaAAButton
              returnUrl={returnUrl}
              fiTypes={["MUTUAL_FUNDS"]}
              className="mt-4"
              size="lg"
              label="Connect Your Accounts"
            />
            <p className="mt-3 text-xs text-muted-foreground">
              Takes less than 2 minutes
            </p>
          </div>

          {/* How It Works */}
          <div>
            <h3 className="mb-4 text-center text-lg font-semibold text-foreground">
              How it works
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex flex-col items-center text-center gap-2 rounded-lg border border-border p-4">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-bold">
                  1
                </div>
                <ListChecks className="h-5 w-5 text-primary" />
                <p className="text-sm font-medium">Choose your accounts</p>
                <p className="text-xs text-muted-foreground">
                  Select which mutual fund accounts to link
                </p>
              </div>
              <div className="flex flex-col items-center text-center gap-2 rounded-lg border border-border p-4">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-bold">
                  2
                </div>
                <ShieldCheck className="h-5 w-5 text-primary" />
                <p className="text-sm font-medium">Approve on Saafe</p>
                <p className="text-xs text-muted-foreground">
                  Review and approve the data sharing consent
                </p>
              </div>
              <div className="flex flex-col items-center text-center gap-2 rounded-lg border border-border p-4">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-bold">
                  3
                </div>
                <Download className="h-5 w-5 text-primary" />
                <p className="text-sm font-medium">Portfolio imported</p>
                <p className="text-xs text-muted-foreground">
                  Your holdings appear automatically in Compass
                </p>
              </div>
            </div>
          </div>

          {/* Features — responsive grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <FeatureItem
              icon={<Building2 className="h-5 w-5 text-primary" />}
              title="Connect Multiple Accounts"
              description="Link mutual fund holdings from any RTA (CAMS, KFintech) or AMC in one go"
            />
            <FeatureItem
              icon={<Lock className="h-5 w-5 text-primary" />}
              title="Bank-Grade Security"
              description="Your data is encrypted end-to-end. We never see your login credentials"
            />
            <FeatureItem
              icon={<Clock className="h-5 w-5 text-primary" />}
              title="Always Up-to-Date"
              description="Your portfolio stays current with automatic data refresh"
            />
          </div>

          {/* Trust Messaging */}
          <div className="rounded-lg border border-border bg-muted/20 p-4 text-center">
            <p className="text-sm font-medium text-foreground">
              {TRUST_COPY.landing.headline}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {TRUST_COPY.landing.body}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {TRUST_COPY.landing.revoke}
            </p>
          </div>

          {/* Trust Badges */}
          <TrustBadgeGroup />

          {/* Partner Logos */}
          <PartnerLogos />

          {/* Footer Links */}
          <div className="flex flex-wrap justify-center gap-4 border-t border-border pt-4 text-xs text-muted-foreground">
            <a
              href="https://sahamati.org.in/what-is-account-aggregator/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-foreground underline"
            >
              What is Account Aggregator?
            </a>
            <Link to="/" className="hover:text-foreground underline">
              Privacy Policy
            </Link>
            <Link to="/" className="hover:text-foreground underline">
              How we use your data
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
